/**
 * 主线程 ↔ Worker RPC 的线上信封格式与运行时校验。
 *
 * 职责边界：这里只校验「信封」结构（kind/id/method/event 字段齐全且类型正确），
 * payload 的业务结构由各协议层（WS 编解码、IndexedDB 存取）在自己的边界上校验。
 * 设计说明见 docs/worker-arch.md。
 */

/** 方法契约：方法名 → 请求/响应类型。调用方与实现方共用同一份契约接口。 */
export type MethodMap = Record<string, { req: unknown; res: unknown }>;

/** 推送契约：事件名 → 载荷类型（Worker → 主线程单向）。 */
export type PushMap = Record<string, unknown>;

export interface WireError {
  readonly code: string;
  readonly message: string;
}

export type WireRequest = {
  readonly kind: 'req';
  readonly id: number;
  readonly method: string;
  readonly payload: unknown;
};

export type WireResponse =
  | { readonly kind: 'res'; readonly id: number; readonly ok: true; readonly payload: unknown }
  | { readonly kind: 'res'; readonly id: number; readonly ok: false; readonly error: WireError };

export type WirePush = {
  readonly kind: 'push';
  readonly event: string;
  readonly payload: unknown;
};

export type WireMessage = WireRequest | WireResponse | WirePush;

/** RPC 层错误。code 跨线程保留，调用方可据此分支（如 'timeout' / 'disposed'）。 */
export class RpcError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'RpcError';
    this.code = code;
  }
}

/** RPC 层自身使用的错误码；业务错误码由各协议层自定义，不在此枚举。 */
export const RPC_ERROR_CODES = {
  /** 调用超时（默认 15s，可按调用覆盖） */
  timeout: 'rpc/timeout',
  /** 客户端已销毁，所有在途调用被拒绝 */
  disposed: 'rpc/disposed',
  /** 服务端没有注册该方法 */
  unknownMethod: 'rpc/unknown-method',
  /** 服务端 handler 抛出了非 RpcError 的异常 */
  internal: 'rpc/internal',
} as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isWireError(value: unknown): value is WireError {
  return isRecord(value) && typeof value.code === 'string' && typeof value.message === 'string';
}

/**
 * 校验收到的消息是否为合法信封。不合法返回 null（调用方丢弃并告警），
 * 不抛错——postMessage 通道上出现垃圾不应击穿整个通信层。
 */
export function parseWireMessage(data: unknown): WireMessage | null {
  if (!isRecord(data)) return null;
  switch (data.kind) {
    case 'req':
      if (typeof data.id === 'number' && typeof data.method === 'string' && 'payload' in data) {
        return { kind: 'req', id: data.id, method: data.method, payload: data.payload };
      }
      return null;
    case 'res':
      if (typeof data.id !== 'number') return null;
      if (data.ok === true && 'payload' in data) {
        return { kind: 'res', id: data.id, ok: true, payload: data.payload };
      }
      if (data.ok === false && isWireError(data.error)) {
        return { kind: 'res', id: data.id, ok: false, error: data.error };
      }
      return null;
    case 'push':
      if (typeof data.event === 'string' && 'payload' in data) {
        return { kind: 'push', event: data.event, payload: data.payload };
      }
      return null;
    default:
      return null;
  }
}

/**
 * 通信端口的最小形状：Worker、DedicatedWorkerGlobalScope（self）、MessagePort
 * 都结构性满足。测试用 MessageChannel 两端回环。
 */
export interface RpcPort {
  postMessage(message: unknown, transfer?: Transferable[]): void;
  addEventListener(type: 'message', listener: (ev: MessageEvent) => void): void;
  removeEventListener(type: 'message', listener: (ev: MessageEvent) => void): void;
  /** MessagePort 用 addEventListener 时必须 start()；Worker/self 没有这个方法。 */
  start?(): void;
}
